import type { CSSProperties } from 'react'
import { Button } from './Button'

interface SaveBarProps {
  changeCount: number
  onDiscard: () => void
  onSave: () => void
  style?: CSSProperties
}

/**
 * Sticky bottom bar with change count + Discard button + Save button.
 * Save is disabled when changeCount === 0.
 * Rule 09: summarises edits before commit.
 */
export function SaveBar({ changeCount, onDiscard, onSave, style }: SaveBarProps): JSX.Element {
  const saveDisabled = changeCount === 0

  return (
    <div
      data-testid="save-bar"
      style={{
        position: 'sticky',
        bottom: 0,
        marginTop: 14,
        background: 'var(--surface)',
        color: 'var(--text)',
        border: '1px solid var(--line-strong)',
        borderRadius: 'var(--r-md)',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        flexWrap: 'wrap',
        boxShadow: '0 4px 14px rgba(80, 56, 28, 0.08)',
        ...style,
      }}
    >
      <span
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}
        data-testid="save-bar-count"
      >
        {changeCount} change{changeCount === 1 ? '' : 's'}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="secondary"
          size="sm"
          data-testid="save-bar-discard"
          onClick={onDiscard}
        >
          Discard
        </Button>
        <Button
          variant="primary"
          size="sm"
          data-testid="save-bar-save"
          onClick={onSave}
          disabled={saveDisabled}
          aria-disabled={saveDisabled}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
